import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { User, Bell, Monitor, Globe, Shield, Zap, Link as LinkIcon, Sun, Moon, Check, ArrowRight } from '../../../icons';
import { Modal, ModalActions, ModalFootNote } from '../../../components/shared';
import { useThemeStore } from '../../../store/themeStore';
import { THEMES, THEME_KEYS, type ThemeKey, type ThemeMode } from '../../../styles/themes';

/**
 * 개인 설정 모달 — all-frame.html #2e.
 *
 * 화면 모드·강조 색상은 **기존 `themeStore` 를 그대로 조작**한다 — 설정값을 따로 갖지 않으므로
 * 설정 모달(`SettingsModal`)에서 바꾸든 여기서 바꾸든 같은 곳이 바뀐다(설정 이원화 없음).
 * ★ 2026-09-07 — 옛 「내 프로필 화면」(`/me`)은 없앴다. 그 내용은 설정 모달에 있다.
 * 사이드바 고정도 마찬가지로 기존 `sidebarStore` 를 쓴다.
 *
 * 아직 저장소가 없는 항목(정보 밀도·애니메이션 줄이기)은 모달 안에서만 상태를 들고,
 * 화면에 '준비 중' 을 명시한다 — 켜지는 척하는 토글을 만들지 않는다.
 */

type CategoryKey = 'account' | 'notification' | 'theme' | 'locale' | 'privacy' | 'shortcut' | 'linked';

const CATEGORIES: Array<{ key: CategoryKey; label: string; icon: typeof User; route?: string }> = [
  { key: 'account', label: '계정 · 프로필', icon: User, route: '/profile' },
  { key: 'notification', label: '알림', icon: Bell, route: '/profile' },
  { key: 'theme', label: '테마 · 화면', icon: Monitor },
  { key: 'locale', label: '언어 · 시간대', icon: Globe, route: '/profile' },
  { key: 'privacy', label: '개인정보 · 보안', icon: Shield, route: '/profile' },
  { key: 'shortcut', label: '바로가기 · 서비스', icon: Zap, route: '/settings' },
  { key: 'linked', label: '연결된 계정', icon: LinkIcon, route: '/profile' },
];

const MODES: Array<{ key: ThemeMode; label: string; icon: typeof Sun }> = [
  { key: 'light', label: '라이트', icon: Sun },
  { key: 'dark', label: '다크', icon: Moon },
  { key: 'auto', label: '시스템 설정', icon: Monitor },
];

export function PersonalSettingsModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const theme = useThemeStore((s) => s.theme);
  const mode = useThemeStore((s) => s.mode);
  const setTheme = useThemeStore((s) => s.setTheme);
  const setMode = useThemeStore((s) => s.setMode);

  const [category, setCategory] = useState<CategoryKey>('theme');

  const goTo = (route: string) => { onClose(); navigate(route); };

  return (
    <Modal
      title="개인 설정"
      size="lg"
      bodyFlat
      onClose={onClose}
      footer={
        <>
          <ModalFootNote>변경사항은 자동 저장됩니다</ModalFootNote>
          <ModalActions>
            <button type="button" className="dw-btn" onClick={() => { setTheme('blue'); setMode('light'); }}>기본값 복원</button>
            <button type="button" className="dw-btn dw-btn--primary" onClick={onClose}>완료</button>
          </ModalActions>
        </>
      }
    >
      <nav className="dw-set__nav" aria-label="설정 카테고리">
        {CATEGORIES.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            className={`dw-set__item${category === key ? ' is-active' : ''}`}
            onClick={() => setCategory(key)}
          >
            <Icon size={15} aria-hidden />{label}
          </button>
        ))}
      </nav>

      <div className="dw-set__body scrollbar-thin">
        {category === 'theme'
          ? (
            <>
              <div>
                <div className="dw-set__h">테마 · 화면</div>
                <div className="dw-set__desc">화면 모드와 강조 색상을 취향에 맞게 설정하세요.</div>
              </div>

              <div className="dw-field">
                <span className="dw-field__label">화면 모드</span>
                <div className="dw-opt-row">
                  {MODES.map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      type="button"
                      className={`dw-opt${mode === key ? ' is-on' : ''}`}
                      aria-pressed={mode === key}
                      onClick={() => setMode(key)}
                    >
                      {mode === key ? <Check size={14} aria-hidden /> : <Icon size={14} aria-hidden />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="dw-field">
                <span className="dw-field__label">강조 색상</span>
                <div className="dw-swatches">
                  {THEME_KEYS.map((key: ThemeKey) => (
                    <button
                      key={key}
                      type="button"
                      className={`dw-sw${theme === key ? ' is-on' : ''}`}
                      style={{ background: THEMES[key].primaryLight }}
                      aria-label={THEMES[key].label}
                      aria-pressed={theme === key}
                      onClick={() => setTheme(key)}
                    />
                  ))}
                </div>
              </div>

              <div className="dw-toggles">
                {/* 2026-09-07 (사용자 지시) — 「사이드바 아이콘만 표시」 스위치를 걷어냈다.
                    접기의 진입점은 **레일 머리의 버튼 하나**(정본 H1-4)와 `Ctrl/Cmd+B` 다.
                    같은 값을 두 곳에서 켜면 문구가 갈라지고, 레일에 버튼이 생긴 뒤로는
                    이 자리가 «찾아 들어가야 하는 두 번째 길» 이 된다. */}
                <div className="dw-tg dw-tg--pending">
                  <div className="dw-tg__main">
                    <div className="dw-tg__t">정보 밀도 · 컴팩트</div>
                    <div className="dw-tg__s">목록·위젯 간격을 좁게 표시 (준비 중)</div>
                  </div>
                  <button type="button" className="dw-switch" role="switch" aria-checked="false" aria-label="정보 밀도 컴팩트" disabled />
                </div>
                <div className="dw-tg dw-tg--pending">
                  <div className="dw-tg__main">
                    <div className="dw-tg__t">애니메이션 줄이기</div>
                    <div className="dw-tg__s">운영체제의 '동작 줄이기' 설정을 그대로 따릅니다</div>
                  </div>
                  <button type="button" className="dw-switch" role="switch" aria-checked="false" aria-label="애니메이션 줄이기" disabled />
                </div>
              </div>
            </>
          )
          : (
            <>
              <div>
                <div className="dw-set__h">{CATEGORIES.find((c) => c.key === category)?.label}</div>
                <div className="dw-set__desc">이 설정은 전용 화면에서 관리합니다.</div>
              </div>
              <button
                type="button"
                className="dw-btn dw-btn--primary"
                onClick={() => goTo(CATEGORIES.find((c) => c.key === category)?.route ?? '/profile')}
              >
                설정 화면 열기<ArrowRight size={14} aria-hidden />
              </button>
            </>
          )}
      </div>
    </Modal>
  );
}
